'use client';

import React from 'react';
import styled from 'styled-components';
import { theme } from '@/config/theme';

/**
 * Checkbox Component Props
 */
export interface CheckboxProps {
  /** Label text displayed next to the checkbox */
  label?: string;
  /** Checked state of the checkbox */
  checked: boolean;
  /** Callback function when checkbox state changes */
  onChange: (checked: boolean) => void;
  /** Indeterminate state (shows horizontal bar when some items are selected) */
  indeterminate?: boolean;
  /** Disabled state */
  disabled?: boolean;
  /** Optional help text displayed below the label */
  helpText?: string;
  /** Optional CSS class name */
  className?: string;
  /** Unique identifier for the input */
  id?: string;
  /** Name attribute for the input */
  name?: string;
  /** Aria label for accessibility (required if label is not provided) */
  'aria-label'?: string;
}

// ============================================================================
// Styled Components
// ============================================================================

const CheckboxContainer = styled.label<{ $disabled?: boolean }>`
  display: inline-flex;
  align-items: center;
  cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};
  user-select: none;
  gap: 10px;
  opacity: ${({ $disabled }) => ($disabled ? 0.5 : 1)};
  
  &:hover {
    opacity: ${({ $disabled }) => ($disabled ? 0.5 : 0.9)};
  }
`;

const HiddenCheckbox = styled.input.attrs({ type: 'checkbox' })`
  position: absolute;
  opacity: 0;
  width: 0;
  height: 0;
  pointer-events: none;
`;

const StyledCheckbox = styled.div<{ $checked: boolean; $indeterminate?: boolean; $disabled?: boolean }>`
  width: 20px;
  height: 20px;
  min-width: 20px;
  min-height: 20px;
  background: ${({ $checked, $indeterminate, theme }) =>
    $checked || $indeterminate
      ? theme.colors.gradients.primaryAlt
      : theme.colors.white};
  border: 1px solid ${({ $checked, $indeterminate, theme }) => 
    ($checked || $indeterminate) ? theme.colors.primary.accent : theme.colors.gray[200]};
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  position: relative;
  margin-top: 0;

  ${HiddenCheckbox}:focus + & {
    outline: 2px solid ${({ theme }) => theme.colors.overlay.focusDark};
    outline-offset: 2px;
  }

  ${CheckboxContainer}:hover & {
    border-color: ${({ $checked, $indeterminate, $disabled, theme }) =>
      $disabled ? theme.colors.gray[200] : ($checked || $indeterminate) ? theme.colors.primary.accent : theme.colors.border.tertiary};
    box-shadow: ${({ $disabled, theme }) => ($disabled ? 'none' : theme.shadows.accent)};
  }

  svg {
    opacity: ${({ $checked, $indeterminate }) => ($checked && !$indeterminate ? 1 : 0)};
    transform: scale(${({ $checked, $indeterminate }) => ($checked && !$indeterminate ? 1 : 0.5)});
    transition: all 0.2s ease;
  }

  /* Indeterminate state - horizontal bar */
  &::after {
    content: '';
    position: absolute;
    left: 3px;
    top: 8px;
    width: 12px;
    height: 2px;
    background: ${({ $indeterminate, theme }) => $indeterminate ? theme.colors.white : 'transparent'};
    border-radius: 1px;
    transition: all 0.2s ease;
  }
`;

const CheckIcon = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
    <path
      d="M11.6666 3.5L5.24992 9.91667L2.33325 7"
      stroke="white"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const LabelContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const LabelText = styled.span`
  font-size: 14px;
  font-weight: 400;
  color: ${theme.colors.gray[900]};
  line-height: 1.4;
`;

const HelpText = styled.span`
  font-size: 12px;
  font-weight: 400;
  color: ${theme.colors.gray[400]};
  line-height: 1.4;
`;

// ============================================================================
// Component
// ============================================================================

/**
 * Checkbox Component
 * 
 * A styled checkbox component with label and optional help text,
 * following the ObservePro design system.
 * 
 * @component
 * @example
 * ```tsx
 * <Checkbox
 *   label="Case Sensitive Search"
 *   checked={isCaseSensitive}
 *   onChange={setIsCaseSensitive}
 * />
 * 
 * // With help text
 * <Checkbox
 *   label="Enable Advanced Mode"
 *   checked={advancedMode}
 *   onChange={setAdvancedMode}
 *   helpText="Enable this to access additional filtering options"
 * />
 * ```
 */
export const Checkbox: React.FC<CheckboxProps> = ({
  label,
  checked,
  onChange,
  indeterminate = false,
  disabled = false,
  helpText,
  className,
  id,
  name,
  'aria-label': ariaLabel,
}) => {
  const checkboxRef = React.useRef<HTMLInputElement>(null);

  React.useEffect(() => {
    if (checkboxRef.current) {
      checkboxRef.current.indeterminate = indeterminate;
    }
  }, [indeterminate]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!disabled) {
      onChange(e.target.checked);
    }
  };

  const checkboxId = id || (label ? `checkbox-${label.replace(/\s+/g, '-').toLowerCase()}` : undefined);
  const labelText = label || ariaLabel;

  return (
    <CheckboxContainer $disabled={disabled} className={className}>
      <HiddenCheckbox
        ref={checkboxRef}
        id={checkboxId}
        name={name}
        checked={checked}
        onChange={handleChange}
        disabled={disabled}
        aria-label={labelText}
      />
      <StyledCheckbox $checked={checked} $indeterminate={indeterminate} $disabled={disabled}>
        <CheckIcon />
      </StyledCheckbox>
      {label && (
        <LabelContent>
          <LabelText>{label}</LabelText>
          {helpText && <HelpText>{helpText}</HelpText>}
        </LabelContent>
      )}
    </CheckboxContainer>
  );
};

export default Checkbox;

